import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'
import { Button } from 'components/styled/button'
import { browserHistory } from 'react-router'
import find from 'lodash/fp/find'
import { mapLanguageToComponentProps } from 'interview/i18n'
import CandidateService from 'interview/services/candidate'
import { completeInterview } from 'interview/actions/candidate'
import InterviewFormWrapper from 'interview/components/interview-form-wrapper'
import * as analytics from 'analytics/performance'
import ActionBarWrapper from 'components/action-bar-wrapper'
import { Body } from 'components/styled/typography'
import { Confirmation } from 'components/styled/modal/confirmation'
import { formatDateTimeTimezone } from 'utils/date'
import {
  getCandidateInfoForAmplitude,
  getCompanyAndInterviewInfoForAmplitude,
} from 'selectors'
import { MODAL_CLOSE_DELAY_MS } from 'components/styled/modal/modal-component'

const propTypes = {
  candidate: PropTypes.shape({
    uuid: PropTypes.string,
    _id: PropTypes.string,
    videoResponses: PropTypes.array,
    agreeTOS: PropTypes.bool,
  }).isRequired,
  interview: PropTypes.shape({
    questions: PropTypes.array,
    formSetup: PropTypes.object,
    language: PropTypes.string,
    additionalFormFields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        required: PropTypes.bool,
      })
    ).isRequired,
    company: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      privacyLink: PropTypes.string,
    }).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  videoUploads: PropTypes.arrayOf(PropTypes.string).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  recording: PropTypes.bool.isRequired,
  model: PropTypes.string,
  onSubmit: PropTypes.func,
  onContinueLater: PropTypes.func,
}

const defaultProps = {
  model: 'candidate',
  onSubmit: undefined,
  onContinueLater: undefined,
}

const mapStateToProps = (state, ownProps) => ({
  candidate:
    ownProps.model === 'previewCandidate'
      ? state.previewCandidate
      : state.candidate,
  interview:
    ownProps.model === 'previewCandidate'
      ? state.interview
      : state.interview.jobOpening,
  videoUploads: state.global.videoUploads,
  recording: state.global.recording,
})

export class Interview extends Component {
  constructor(props) {
    super(props)

    this.state = {
      missingVideosConfirmed: false,
      askConfirmation: false,
      infoUpdated: false,
      isLoading: false,
      shouldSubmitAdditionalFields: false,
    }

    this.onSubmit = this.onSubmit.bind(this)
    this.onConfirm = this.onConfirm.bind(this)
    this.continueLater = this.continueLater.bind(this)
  }

  /**
   * When submitted change interview completed
   */
  onSubmit() {
    const { candidate, interview, model } = this.props
    const { missingVideosConfirmed } = this.state
    if (model === 'previewCandidate') {
      this.setState({ askConfirmation: true })
    } else {
      const companyAndInterviewInfo =
        getCompanyAndInterviewInfoForAmplitude(interview)
      const candidateInfo = getCandidateInfoForAmplitude(candidate)
      const missingAnswers = !this.areAllAnswered() && !missingVideosConfirmed

      this.setState({ askConfirmation: true })
      analytics.onModalLoadEvent({
        locationId: 'interviewPage',
        id: missingAnswers ? 'confirmMissingVideos' : 'confirmSubmit',
        properties: {
          ...candidateInfo,
          ...companyAndInterviewInfo,
        },
      })
    }
  }

  onConfirmSubmit() {
    const { candidate, dispatch, model, onSubmit, interview } = this.props
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)

    if (model === 'previewCandidate' && onSubmit) {
      setTimeout(() => {
        onSubmit()
      }, MODAL_CLOSE_DELAY_MS)
    } else {
      this.setState({ isLoading: true })
      analytics.onClickEvent({
        locationId: 'interviewPage',
        id: 'submitInterviewButton',
        properties: {
          ...candidateInfo,
          ...companyAndInterviewInfo,
        },
      })
      dispatch(completeInterview(candidate)).then(() => {
        this.setState({ isLoading: false })
        if (candidate.completeUrl) {
          window.location.href = candidate.completeUrl
        } else {
          browserHistory.push('/enter/finished')
          dispatch(actions.change('candidate', { uuid: candidate.uuid }))
        }
      })
    }
  }

  onConfirm() {
    const { interview, candidate, model } = this.props
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)
    this.setState(
      {
        missingVideosConfirmed: true,
        askConfirmation: false,
      },
      () => this.onConfirmSubmit()
    )

    if (model === 'candidate') {
      analytics.onClickEvent({
        locationId: 'interviewPage',
        id: 'confirmSendButton',
        properties: {
          ...candidateInfo,
          ...companyAndInterviewInfo,
        },
      })
    }
  }

  areAllAnswered() {
    const {
      candidate: { videoResponses },
      interview: { questions },
    } = this.props
    let allAnswered = true
    // Check that all videos are answered
    questions.forEach(({ _id: questionId, answerable }) => {
      if (!answerable) {
        return
      }

      const response = find({ questionId })(videoResponses)
      if (!response || !response.has) {
        allAnswered = false
      }
    })

    return allAnswered
  }

  continueLater() {
    const { candidate, model, onContinueLater } = this.props
    if (model === 'previewCandidate') {
      if (onContinueLater) {
        onContinueLater()
      }
    } else if (candidate.completeUrl) {
      window.location.href = candidate.completeUrl
    } else {
      CandidateService.continueLater(candidate.uuid).then(() =>
        browserHistory.push('/saved')
      )
    }
  }

  render() {
    const { candidate, i18n, interview, videoUploads, recording, model } =
      this.props
    const { askConfirmation, isLoading, missingVideosConfirmed } = this.state
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)

    const missingAnswers = !this.areAllAnswered() && !missingVideosConfirmed
    const confirmHeader = missingAnswers
      ? i18n.interview.confirmSubmitHeader
      : i18n.interview.submitInterviewHeader

    const confirmContent = missingAnswers ? (
      <>
        <Body>{i18n.interview.confirmSubmitContent}</Body>
        <Body>{i18n.interview.confirmSubmitContentWarning}</Body>
      </>
    ) : (
      <Body>{i18n.interview.submitInterviewContent}</Body>
    )

    // TODO: action bar date format different from header date format in designs. I use the same  format as header for now
    return (
      <div className="interview">
        <InterviewFormWrapper
          onSubmit={this.onSubmit}
          onConfirm={this.onConfirm}
          model={model}
          updateAdditionalFieldsValidityState={shouldSubmitAdditionalFields => {
            this.setState({ shouldSubmitAdditionalFields })
          }}
        >
          <ActionBarWrapper
            hideBeforeScroll
            leftSideComponent={
              interview.endTime && (
                <Body fontWeight={600}>
                  {i18n.layout.endTime(
                    formatDateTimeTimezone(interview.endTime)
                  )}
                </Body>
              )
            }
            actions={
              <>
                <Button
                  variant="secondary"
                  onClick={this.continueLater}
                  disabled={
                    videoUploads.length > 0 ||
                    recording ||
                    !candidate.agreeTOS ||
                    isLoading
                  }
                  text={i18n.interview.continueLater}
                  icon="access_time"
                  event={
                    model === 'candidate' && {
                      locationId: 'interviewPage',
                      id: 'candidateContinueLaterButton',
                      properties: {
                        ...candidateInfo,
                        ...companyAndInterviewInfo,
                      },
                    }
                  }
                />
                <Button
                  type="submit"
                  onClick={() => document.interviewForm.submit()}
                  loading={isLoading}
                  disabled={
                    videoUploads.length > 0 || recording || !candidate.agreeTOS
                  }
                  text={i18n.interview.submit}
                  icon="send"
                  flippedIcon={interview.language === 'ar'}
                />
              </>
            }
          />
        </InterviewFormWrapper>
        <Confirmation
          id="submit-confirm-modal"
          isOpen={askConfirmation}
          title={confirmHeader}
          body={confirmContent}
          isLoading={isLoading}
          flippedView={interview.language === 'ar'}
          size="medium"
          primaryButtonIcon="send"
          primaryButtonText={i18n.interview.confirmSubmitButton}
          onCancel={() => this.setState({ askConfirmation: false })}
          onConfirm={this.onConfirm}
          useConfirm
        />
      </div>
    )
  }
}

Interview.propTypes = propTypes
Interview.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(Interview)
